import { DateTime } from "luxon";
import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import type { HasMetaData, NormalizedContent } from "@tmi/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

const META_PAGE_SIZE = 5000;
const MAX_CONSECUTIVE_FAILURES = 2;

export interface ContentSyncConfig<TResponse, TContentful> {
    name: string;
    store: () => LocalForage;
    syncKey: string;
    metaQueryId: string;
    contentQueryId: string;
    contentPageSize: number;
    metaContentFilter?: Record<string, unknown>;
    extractMetaPayload: ( data: TResponse ) => TContentful[] | undefined | null | void;
    extractContentPayload: ( data: TResponse ) => TContentful[] | undefined | null;
    normalizeItem: ( item: TContentful ) => NormalizedContent | undefined;
}

export class ContentSyncCoordinator<TResponse, TContentful> {
    readonly #config: ContentSyncConfig<TResponse, TContentful>;

    constructor( config: ContentSyncConfig<TResponse, TContentful> ) {
        this.#config = config;
    }

    async patchRepository( authToken: string ): Promise<number> {
        let itemsUpdated = 0;
        const cfg = this.#config;

        try {
            if ( typeof syncState === 'undefined' ) {
                await LocalForagePlugin.initialize();
            }

            const syncThresholdInMillis = await syncState.getItem<number>( cfg.syncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                logger.debug( `[ ${ cfg.name }-sync-coordinator ] load all available ${ cfg.name } meta-data...` );
                const t0 = performance.now();

                const allMetadata = await this.refreshMetaDataFromServer( authToken );

                if ( 0 === allMetadata.length ) {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( cfg.syncKey, nextSyncThresholdInMillis );
                    return itemsUpdated;
                }

                // Set throttle after successful fetch
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( cfg.syncKey, nextSyncThresholdInMillis );

                const t1 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } ${ cfg.name } meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map<string, HasMetaData>(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                const t2 = performance.now();

                const store = cfg.store();
                if ( await store.length() > 0 ) {
                    const keysToRemove: string[] = [];

                    await store.iterate( ( content: NormalizedContent, key: string ) => {
                        const meta: HasMetaData | undefined = allMetaDataByIdMap.get( key );
                        if ( typeof meta !== 'undefined' ) {
                            if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                                allMetaDataByIdMap.delete( key );
                            } else {
                                keysToRemove.push( key );
                            }
                        } else {
                            keysToRemove.push( key );
                        }
                        return;
                    } );

                    await batchRemoveItems( store, keysToRemove );
                }

                const t3 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Aggregated missing ${ cfg.name } in ${ t3 - t2 } ms` );

                const missingIds = Array.from( allMetaDataByIdMap.keys() );
                if ( missingIds.length > 0 ) {
                    itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
                }

                const t4 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Processing all missing ${ cfg.name } took ${ t4 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( `[ ${ cfg.name }-sync-coordinator ] Sync failed:`, err );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise<HasMetaData[]> {
        let page = 0;
        let consecutiveFailures = 0;
        const cfg = this.#config;

        const metadata: HasMetaData[] = [];
        let metaPayload: TContentful[] | undefined | null | void = [];

        do {
            const variables: Record<string, unknown> = {
                skip: page,
                limit: META_PAGE_SIZE
            };
            if ( cfg.metaContentFilter ) {
                variables.contentFilter = cfg.metaContentFilter;
            }

            const response =
                await goFetch<TResponse, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: cfg.metaQueryId,
                        variables
                    },
                    authToken
                } );

            if ( !response ) {
                consecutiveFailures++;
                logger.warn( `${ cfg.name } metadata fetch failed (${ consecutiveFailures }/${ MAX_CONSECUTIVE_FAILURES } consecutive failures)` );

                if ( consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ) {
                    logger.warn( `Too many consecutive failures, stopping ${ cfg.name } metadata pagination` );
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = cfg.extractMetaPayload( response.data );
            if ( metaPayload ) {
                ( metaPayload as TContentful[] ).forEach( ( item: TContentful ) => {
                    const a = item as any;
                    metadata.push( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } );
                } );
            }

            page++;
        } while ( ( ( metaPayload as any[] | undefined )?.length ?? 0 ) >= META_PAGE_SIZE );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise<number> {
        const t0 = performance.now();
        let itemsUpdated = 0;
        let consecutiveFailures = 0;
        const cfg = this.#config;

        for ( let offset = 0; offset < missingContentIds.length; offset += cfg.contentPageSize ) {
            const idsForPage = missingContentIds.slice( offset, offset + cfg.contentPageSize );

            const response =
                await goFetch<TResponse, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: cfg.contentQueryId,
                        variables: {
                            skip: 0,
                            limit: cfg.contentPageSize,
                            contentFilter: {
                                ids: idsForPage
                            }
                        }
                    },
                    authToken
                } );

            if ( !response ) {
                consecutiveFailures++;
                logger.warn( `${ cfg.name } content fetch failed (${ consecutiveFailures }/${ MAX_CONSECUTIVE_FAILURES } consecutive failures)` );

                if ( consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ) {
                    logger.warn( `Too many consecutive failures, stopping ${ cfg.name } content pagination` );
                    break;
                }

                continue;
            }

            consecutiveFailures = 0;

            const contentPayload = cfg.extractContentPayload( response.data );
            if ( null != contentPayload ) {
                const normalized: NormalizedContent[] = [];
                contentPayload.forEach( ( item: TContentful ) => {
                    const result = cfg.normalizeItem( item );
                    if ( typeof result !== 'undefined' ) {
                        normalized.push( result );
                    }
                } );

                const store = cfg.store();
                await store.ready();
                try {
                    await batchSetItems( store, normalized.map( c => [ c.contentfulId, c ] as const ) );
                } catch ( err: any ) {
                    logger.error( `[ ${ cfg.name }-sync-coordinator ] Sync failed:`, err );
                }

                const t1 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Processing ${ normalized.length } ${ cfg.name } took ${ t1 - t0 } ms` );

                itemsUpdated += normalized.length;
            }
        }

        return itemsUpdated;
    }
}
